import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Send, Bell, History } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUsd, REMINDER_TONE } from '@/lib/remindersData';

const STATUS_BADGE = {
  overdue:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  due:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
};

const TYPE_BADGE_CLS = {
  default:
    'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
  warning:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  danger:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
};

const reminderTypeLabel = (t, type) =>
  t(`reminders.types.${type}`, { defaultValue: type });

const Row = ({ row, onSend }) => {
  const { t } = useTranslation();
  const reminders = row.reminders_sent || [];
  const last = reminders[reminders.length - 1] || null;
  return (
    <div
      className="grid grid-cols-12 gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors"
      data-testid={`reminder-line-${row.row_key}`}
    >
      <div className="col-span-12 sm:col-span-4 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {row.job_name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {row.client || t('reminders.list.noClient')} {'\u00b7'} {row.invoice_desc}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <Badge
          variant="outline"
          className={cn('uppercase text-[10px] tracking-[0.16em]', STATUS_BADGE[row.derived_status] || STATUS_BADGE.due)}
        >
          {row.derived_status === 'overdue'
            ? t('reminders.list.statusOverdue')
            : t('reminders.list.statusDueSoon')}
        </Badge>
        <div className="text-[11px] text-muted-foreground mt-1">
          {row.derived_status === 'overdue'
            ? t('reminders.list.pastDue', { count: row.days_past_due })
            : row.days_until_due != null
            ? t('reminders.list.inDays', { count: row.days_until_due })
            : row.due || t('reminders.list.tbd')}
        </div>
      </div>
      <div className="col-span-3 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('reminders.list.amount')}
        </div>
        <div className="text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(row.amount)}
        </div>
      </div>
      <div className="col-span-3 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('reminders.list.lastSent')}
        </div>
        {last ? (
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge
              variant="outline"
              className={cn('uppercase text-[10px] tracking-[0.16em]', TYPE_BADGE_CLS[REMINDER_TONE[last.type]])}
            >
              {reminderTypeLabel(t, last.type)}
            </Badge>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t('reminders.list.never')}</div>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {t('reminders.list.countSent', { count: reminders.length })}
        </div>
      </div>
      <div className="col-span-12 sm:col-span-2 flex items-center justify-end">
        <Button
          size="sm"
          onClick={() => onSend(row)}
          className="h-8 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-[10px]"
          data-testid={`reminder-send-${row.row_key}`}
        >
          <Send size={11} className="mr-1.5" />
          {reminders.length === 0 ? t('reminders.list.sendFirst') : t('reminders.list.followUp')}
        </Button>
      </div>
    </div>
  );
};

const ListSkeleton = () => (
  <div className="divide-y divide-border" data-testid="reminders-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-24 ml-auto" />
      </div>
    ))}
  </div>
);

export const RemindersList = ({
  rows,
  loading,
  statusFilter,
  onStatusFilter,
  search,
  onSearch,
  onSend,
}) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="reminders-table-card"
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3">
          <Tabs value={statusFilter} onValueChange={onStatusFilter} className="w-full sm:w-auto">
            <TabsList
              className="bg-[hsl(var(--muted))] border border-border w-full sm:w-auto overflow-x-auto no-scrollbar"
              data-testid="reminders-status-tabs"
            >
              <TabsTrigger value="all" className="text-xs uppercase tracking-[0.16em]">
                {t('reminders.list.tabs.all')}
              </TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs uppercase tracking-[0.16em]">
                {t('reminders.list.tabs.overdue')}
              </TabsTrigger>
              <TabsTrigger value="due" className="text-xs uppercase tracking-[0.16em]">
                {t('reminders.list.tabs.due')}
              </TabsTrigger>
              <TabsTrigger value="unsent" className="text-xs uppercase tracking-[0.16em]">
                {t('reminders.list.tabs.unsent')}
              </TabsTrigger>
              <TabsTrigger value="sent" className="text-xs uppercase tracking-[0.16em]">
                {t('reminders.list.tabs.sent')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('reminders.list.searchPlaceholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="reminders-search"
              />
            </div>
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="col-span-4">{t('reminders.list.colJobClient')}</span>
          <span className="col-span-2">{t('reminders.list.colStatus')}</span>
          <span className="col-span-2">{t('reminders.list.colAmount')}</span>
          <span className="col-span-2">{t('reminders.list.colLast')}</span>
          <span className="col-span-2 text-right">{t('reminders.list.colActions')}</span>
        </div>

        {loading ? (
          <ListSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="reminders-empty"
          >
            <Bell size={20} className="text-muted-foreground/70" />
            {t('reminders.list.empty')}
          </div>
        ) : (
          <div data-testid="reminders-list">
            {rows.map((row) => (
              <Row key={row.row_key} row={row} onSend={onSend} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const RemindersHistoryCard = ({ history = [] }) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="reminders-history"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History size={14} className="text-[hsl(var(--primary))]" />
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('reminders.history.title')}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('reminders.history.count', { count: history.length })}
          </span>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {t('reminders.history.empty')}
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-auto kc-scroll pr-1">
            {history.slice(0, 30).map((h) => (
              <div
                key={h.id}
                className="rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <Badge
                    variant="outline"
                    className={cn('uppercase text-[10px] tracking-[0.16em]', TYPE_BADGE_CLS[REMINDER_TONE[h.type]])}
                  >
                    {reminderTypeLabel(t, h.type)}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">
                    {h.sent_at ? new Date(h.sent_at).toLocaleString() : ''}
                  </span>
                </div>
                <div className="mt-1.5 text-xs font-semibold text-foreground truncate">
                  {h.job_name}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {h.client || t('reminders.history.noClient')} {'\u00b7'} {h.invoice_desc} {'\u00b7'} {formatUsd(h.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RemindersList;
